import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import Forms from '../../../components/Forms';
import PhotoGallery from '../../../components/PhotoGallery';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import routes from '../../../routesConfig';
import {useAuth} from '../../../contexts/AuthContext';

const STATUS_OPTIONS = [
    {value: 'intake', label: 'Přijato'},
    {value: 'repairing', label: 'V opravě'},
    {value: 'repaired', label: 'Opraveno'},
    {value: 'listed', label: 'Vystaveno k prodeji'},
    {value: 'sold', label: 'Prodáno'},
    {value: 'stocked', label: 'Naskladněno'},
];

const SOURCE_TYPE_OPTIONS = [
    {value: 'customer', label: 'Zákazník'},
    {value: 'external_marketplace', label: 'Marketplace'},
    {value: 'internal', label: 'Interní'}
];

const MARKETPLACE_OPTIONS = [
    {value: 'bazos', label: 'Bazoš'},
    {value: 'aukro', label: 'Aukro'},
    {value: 'facebook_marketplace', label: 'Facebook Marketplace'},
    {value: 'sbazar', label: 'Sbazar'},
    {value: 'other', label: 'Jiné'}
];

const ACCESSORY_OPTIONS = [
    {value: 'box', label: 'Krabička'},
    {value: 'charger', label: 'Nabíječka'},
    {value: 'cable', label: 'Kabel'},
    {value: 'case', label: 'Obal'},
    {value: 'other', label: 'Jiné'}
];

const CONDITION_OPTIONS = ['A', 'B', 'C', 'D', 'E'].map(v => ({value: v, label: v}));
const PAYMENT_TYPE_OPTIONS = [
    {value: 'paid', label: 'Zaplaceno'},
    {value: 'cod', label: 'Na dobírku'},
    {value: 'free', label: 'Zdarma'}
];
const INTAKE_PURPOSE_OPTIONS = [
    {value: 'repair', label: 'Zařízení je určeno k opravě'},
    {value: 'donor', label: 'Zařízení je určeno jako soubor náhradních dílů'}
];

const DEFAULT_FORM = {
    buybackNumber: '',
    dealAt: new Date().toISOString(),
    status: 'agreed',
    intakePurpose: 'repair',
    source: {
        type: 'external_marketplace',
        customerId: null,
        marketplace: 'bazos',
        sellerName: '',
        sellerContact: '',
        externalUrl: '',
        note: ''
    },
    acquiredBy: null,
    receivedBy: null,
    device: null,
    deviceSnapshot: {name: '', code: ''},
    item: {
        color: '',
        storage: null,
        identifiers: {
            imei: '',
            serial: '',
            sn: '',
            other: ''
        },
        accessories: [],
        locks: {
            icloud: false,
            frp: false,
            sim: false,
            bios: false
        },
        condition: {
            grade: 'C',
            batteryHealthPct: null,
            cosmeticNote: '',
            functionalNote: '',
            waterDamage: false
        },
        donorParts: [],
        photoIds: [],
        photoAssets: []
    },
    economics: {
        purchasePrice: null,
        paymentType: 'paid'
    },
    links: {
        repairId: '',
        inventoryItemId: '',
        saleOrderId: ''
    }
};

const BuybackEditor = () => {
    const {t} = useTranslation();
    const {user} = useAuth();
    const {id} = useParams();
    const navigate = useNavigate();

    const isEdit = Boolean(id && id !== 'new');
    const [searchParams] = useSearchParams();
    // Výkup z poptávky na webu (Požadavky z webu → „vytvořit výkup“)
    const inquiryId = !isEdit ? searchParams.get('inquiryId') : null;
    const [inquiry, setInquiry] = useState(null);
    const [form, setForm] = useState(DEFAULT_FORM);
    const [devices, setDevices] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [photoActionLoading, setPhotoActionLoading] = useState(false);
    const [pendingPhotoFiles, setPendingPhotoFiles] = useState([]);
    const pendingPhotoFilesRef = useRef([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        pendingPhotoFilesRef.current = pendingPhotoFiles;
    }, [pendingPhotoFiles]);

    useEffect(() => () => {
        pendingPhotoFilesRef.current.forEach((entry) => revokePendingPreview(entry?.previewUrl));
    }, []);

    const setF = (path, value) => {
        setForm(prev => {
            const clone = cloneDeep(prev);
            setDeep(clone, path, value);
            return clone;
        });
    };

    const deviceOptions = useMemo(
        () => devices
            .map(d => ({
                value: d._id,
                label: `${d.name}${d.code ? ` (${d.code})` : ''}`,
                data: d
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'cs', {sensitivity: 'base'})),
        [devices]
    );

    const customerOptions = useMemo(
        () => customers.map(c => ({
            value: c._id,
            label: customerLabel(c),
            data: c
        })),
        [customers]
    );

    const userOptions = useMemo(
        () => users.map((user) => ({
            value: user._id,
            label: `${user.name || ''} ${user.surname || ''}`.trim() || user.email || user._id,
            data: user
        })),
        [users]
    );

    const fetchDependencies = useCallback(async () => {
        const [devicesRes, customersRes, usersRes] = await Promise.all([
            API.get(API_ENDPOINTS.device.list),
            API.get(API_ENDPOINTS.customer.list),
            API.get(API_ENDPOINTS.user.list)
        ]);
        setDevices(devicesRes.data || []);
        setCustomers(customersRes.data || []);
        setUsers(usersRes.data || []);
    }, []);

    const fetchDetail = useCallback(async () => {
        if (!isEdit) {
            return;
        }
        const res = await API.get(API_ENDPOINTS.buybacks.detail(id));
        setForm(normalizeIncoming(res.data || DEFAULT_FORM));
        clearPendingPhotos(setPendingPhotoFiles);
    }, [id, isEdit]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                await fetchDependencies();
                await fetchDetail();
                if (inquiryId) {
                    const {data} = await API.get(API_ENDPOINTS.webInquiry.detail(inquiryId));
                    setInquiry(data);
                    setForm(prev => {
                        const next = cloneDeep(prev);
                        const customerId = data?.customer?._id || data?.customer || null;
                        if (customerId) {
                            next.source.type = 'customer';
                            next.source.customerId = customerId;
                        }
                        next.source.note = [
                            `Z poptávky na webu ${data.number}`,
                            data.buyback?.device,
                            data.buyback?.storage,
                            data.buyback?.accessories ? `příslušenství: ${data.buyback.accessories}` : '',
                            data.message
                        ].filter(Boolean).join(' · ');
                        return next;
                    });
                }
                setError(null);
            } catch (e) {
                console.error(e);
                setError(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [fetchDependencies, fetchDetail, inquiryId]);

    useEffect(() => {
        if (isEdit) return;
        const currentUserId = toId(user);
        if (!currentUserId) return;
        setForm((prev) => ({
            ...prev,
            acquiredBy: prev.acquiredBy || currentUserId,
            receivedBy: prev.receivedBy || currentUserId
        }));
    }, [isEdit, user]);

    const selectedDeviceOption = deviceOptions.find(option => option.value === form.device) || null;
    const selectedCustomerOption = customerOptions.find(option => option.value === form.source.customerId) || null;
    const selectedAcquiredByOption = userOptions.find(option => option.value === form.acquiredBy) || null;
    const selectedReceivedByOption = userOptions.find(option => option.value === form.receivedBy) || null;
    const availableColorOptions = useMemo(
        () => normalizeDeviceColorOptions(selectedDeviceOption?.data),
        [selectedDeviceOption?.data]
    );
    const availableStorageOptions = useMemo(
        () => normalizeDeviceStorageOptions(selectedDeviceOption?.data),
        [selectedDeviceOption?.data]
    );
    const selectedColorOption = availableColorOptions.find(option => option.value === (form.item.color || '')) || null;
    const selectedStorageOption = availableStorageOptions.find(option =>
        option.data?.storage === Number(form.item.storage?.storage)
        && option.data?.unit === String(form.item.storage?.unit || '').toUpperCase()
    ) || null;
    const validate = () => {
        if (!form.device) return 'Vyberte zařízení.';
        if (!form.dealAt) return 'Doplňte datum výkupu.';
        if (form.source.type === 'customer' && !form.source.customerId) return 'Vyberte zákazníka.';
        return null;
    };

    const handleDeviceChange = (option) => {
        const nextDeviceId = option?.value || null;
        const nextColorOptions = normalizeDeviceColorOptions(option?.data);
        const nextStorageOptions = normalizeDeviceStorageOptions(option?.data);

        setForm(prev => {
            const next = cloneDeep(prev);
            next.device = nextDeviceId;

            if (nextColorOptions.length && !nextColorOptions.some(item => item.value === next.item.color)) {
                next.item.color = '';
            }

            const nextStorage = normalizeStorageValue(next.item.storage);
            if (nextStorageOptions.length && !nextStorageOptions.some(item =>
                item.data?.storage === nextStorage?.storage && item.data?.unit === nextStorage?.unit
            )) {
                next.item.storage = null;
            }
            return next;
        });
    };

    const onSubmit = async () => {
        const validationError = validate();
        if (validationError) {
            setError({message: validationError});
            return;
        }

        setSaving(true);
        try {
            const payload = sanitizePayload(form, selectedDeviceOption?.data);
            let buybackId = id;

            if (isEdit) {
                await API.put(API_ENDPOINTS.buybacks.update(id), payload);
            } else {
                const res = await API.post(API_ENDPOINTS.buybacks.create, payload);
                buybackId = res?.data?._id || null;
            }

            if (!buybackId) {
                throw new Error('Nepodařilo se určit ID výkupu pro upload fotek.');
            }

            if (pendingPhotoFiles.length) {
                await uploadPhotoFiles(buybackId, pendingPhotoFiles);
                clearPendingPhotos(setPendingPhotoFiles);
            }

            if (inquiryId && !isEdit) {
                await API.post(API_ENDPOINTS.webInquiry.link(inquiryId), {buybackId});
                navigate(routes.admin.getInquiry(inquiryId));
                return;
            }
            navigate(routes.admin.buybacksList);
            setError(null);
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setSaving(false);
        }
    };

    const uploadPhotoFiles = async (buybackId, files) => {
        if (!files?.length) return;
        const formData = new FormData();
        files
            .map((entry) => entry?.file)
            .filter(Boolean)
            .forEach((file) => formData.append('photos', file));
        await API.put(API_ENDPOINTS.buybacks.uploadPhotos(buybackId), formData, {
            headers: {'Content-Type': 'multipart/form-data'}
        });
    };

    const removePhoto = async (photoId) => {
        if (!isEdit || !photoId) return;
        setPhotoActionLoading(true);
        try {
            await API.delete(API_ENDPOINTS.buybacks.deletePhoto(id, photoId));
            await fetchDetail();
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setPhotoActionLoading(false);
        }
    };

    if (loading) {
        return <div>{t('loading')}...</div>;
    }

    const renderActionButtons = () => (
        <div className="flex flex-wrap gap-2">
            <Forms.Button label={t('cancel')} onClick={() => navigate(routes.admin.buybacksList)}/>
            <Forms.Button label={t('save')} onClick={onSubmit} disabled={saving || photoActionLoading}/>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="py-3 border-b border-slate-200 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <h1 className="text-xl font-semibold">{isEdit ? 'Úprava výkupu' : 'Nový výkup'}</h1>
                {renderActionButtons()}
            </div>

            {error && (
                <div className="p-3 rounded bg-red-50 text-red-800 text-sm">
                    {error?.message || 'Nepodařilo se uložit výkup.'}
                </div>
            )}

            {inquiry && (
                <div className="p-3 rounded bg-blue-50 text-blue-900 text-sm">
                    Výkup z poptávky na webu <strong>{inquiry.number}</strong>: {inquiry.buyback?.device}
                    {inquiry.buyback?.storage ? `, ${inquiry.buyback.storage}` : ''}. Vyberte zařízení z katalogu a doplňte cenu –
                    po uložení se poptávka označí jako vykoupená.
                </div>
            )}

            <PrioritySection
                deviceOptions={deviceOptions}
                userOptions={userOptions}
                selectedDeviceOption={selectedDeviceOption}
                selectedAcquiredByOption={selectedAcquiredByOption}
                selectedReceivedByOption={selectedReceivedByOption}
                form={form}
                setF={setF}
                onDeviceChange={handleDeviceChange}
                intakePurpose={form.intakePurpose}
            />

            <Section title="Základ">
                <div className="grid gap-4" style={FORM_GRID_STYLE}>
                    <Forms.Text
                        label="Číslo výkupu"
                        value={form.buybackNumber}
                        onChange={v => setF('buybackNumber', v)}
                        placeholder="Automaticky: BB-YYYYMMDD-XXX"
                    />
                    <Forms.Select
                        label="Stav"
                        value={STATUS_OPTIONS.find(o => o.value === form.status) || null}
                        onChange={option => setF('status', option?.value || 'agreed')}
                        options={STATUS_OPTIONS}
                    />
                </div>
            </Section>

            <Section title="Zdroj">
                <div className="grid gap-4" style={FORM_GRID_STYLE}>
                    <Forms.Select
                        label="Typ zdroje"
                        value={SOURCE_TYPE_OPTIONS.find(o => o.value === form.source.type) || null}
                        onChange={option => setF('source.type', option?.value || 'external_marketplace')}
                        options={SOURCE_TYPE_OPTIONS}
                    />
                    {form.source.type === 'customer' && (
                        <Forms.Select
                            label="Zákazník"
                            value={selectedCustomerOption}
                            onChange={option => setF('source.customerId', option?.value || null)}
                            options={customerOptions}
                        />
                    )}
                    {form.source.type === 'external_marketplace' && (
                        <>
                            <Forms.Select
                                label="Marketplace"
                                value={MARKETPLACE_OPTIONS.find(o => o.value === form.source.marketplace) || null}
                                onChange={option => setF('source.marketplace', option?.value || 'bazos')}
                                options={MARKETPLACE_OPTIONS}
                            />
                            <Forms.Text label="Jméno prodejce" value={form.source.sellerName || ''} onChange={v => setF('source.sellerName', v)}/>
                            <Forms.Text label="Kontakt prodejce" value={form.source.sellerContact || ''} onChange={v => setF('source.sellerContact', v)}/>
                            <Forms.Text label="URL inzerátu" value={form.source.externalUrl || ''} onChange={v => setF('source.externalUrl', v)}/>
                        </>
                    )}
                </div>
                <div className="mt-4">
                    <Forms.Textarea label="Poznámka ke zdroji" value={form.source.note || ''} onChange={v => setF('source.note', v)}/>
                </div>
            </Section>

            <Section title="Zařízení">
                <div className="grid gap-4" style={FORM_GRID_STYLE}>
                    <Forms.Select
                        label="Barva"
                        value={selectedColorOption}
                        options={availableColorOptions}
                        onChange={(option) => setF('item.color', option?.value || '')}
                        disabled={!availableColorOptions.length}
                        placeholder={availableColorOptions.length ? 'Vyberte barvu' : 'Zařízení nemá definované barvy'}
                    />
                    <Forms.Select
                        label="Kapacita"
                        value={selectedStorageOption}
                        options={availableStorageOptions}
                        onChange={(option) => setF('item.storage', option?.data || null)}
                        disabled={!availableStorageOptions.length}
                        placeholder={availableStorageOptions.length ? 'Vyberte kapacitu' : 'Zařízení nemá definované kapacity'}
                    />
                </div>

                <SubSection title="Zámky">
                    <div className="grid gap-3" style={COMPACT_FORM_GRID_STYLE}>
                        <Forms.Checkbox label="iCloud" value={!!form.item.locks.icloud} onChange={v => setF('item.locks.icloud', v)}/>
                        <Forms.Checkbox label="FRP" value={!!form.item.locks.frp} onChange={v => setF('item.locks.frp', v)}/>
                        <Forms.Checkbox label="SIM" value={!!form.item.locks.sim} onChange={v => setF('item.locks.sim', v)}/>
                        <Forms.Checkbox label="BIOS" value={!!form.item.locks.bios} onChange={v => setF('item.locks.bios', v)}/>
                    </div>
                </SubSection>

                <SubSection title="Stav">
                    <div className="grid gap-4" style={FORM_GRID_STYLE}>
                        <Forms.Select
                            label="Třída"
                            value={CONDITION_OPTIONS.find(o => o.value === form.item.condition.grade) || null}
                            onChange={option => setF('item.condition.grade', option?.value || 'C')}
                            options={CONDITION_OPTIONS}
                        />
                        <Forms.Number
                            label="Kondice baterie (%)"
                            value={form.item.condition.batteryHealthPct ?? ''}
                            onChange={v => setF('item.condition.batteryHealthPct', numOrNull(v))}
                        />
                        <Forms.Checkbox
                            label="Poškození vodou"
                            value={!!form.item.condition.waterDamage}
                            onChange={v => setF('item.condition.waterDamage', v)}
                        />
                    </div>
                    <div className="grid gap-4 mt-4" style={FORM_GRID_STYLE}>
                        <Forms.Textarea
                            label="Kosmetické poznámky"
                            value={form.item.condition.cosmeticNote || ''}
                            onChange={v => setF('item.condition.cosmeticNote', v)}
                        />
                        <Forms.Textarea
                            label="Funkční poznámky"
                            value={form.item.condition.functionalNote || ''}
                            onChange={v => setF('item.condition.functionalNote', v)}
                        />
                    </div>
                </SubSection>

                <SubSection title="Příslušenství">
                    <Forms.MultiCheckbox
                        label="Příslušenství"
                        value={form.item.accessories || []}
                        onChange={vals => setF('item.accessories', vals || [])}
                        options={ACCESSORY_OPTIONS}
                    />
                </SubSection>

                <SubSection title="Identifikátory">
                    <div className="grid gap-4" style={FORM_GRID_STYLE}>
                        <Forms.Text label="IMEI" value={form.item.identifiers?.imei || ''} onChange={v => setF('item.identifiers.imei', v)}/>
                        <Forms.Text label="Serial Number" value={form.item.identifiers?.serialNumber || ''} onChange={v => setF('item.identifiers.serialNumber', v)}/>
                    </div>
                </SubSection>

                <SubSection title="Fotky zařízení">
                    <PhotoManager
                        isEdit={isEdit}
                        items={form.item.photoAssets || []}
                        pendingFiles={pendingPhotoFiles}
                        onSelectFiles={async (files) => {
                            const pendingEntries = await buildPendingPhotoEntries(files);
                            setPendingPhotoFiles((prev) => {
                                prev.forEach((entry) => revokePendingPreview(entry?.previewUrl));
                                return pendingEntries;
                            });
                        }}
                        onRemovePendingFile={(itemToRemove) => {
                            setPendingPhotoFiles((prev) => {
                                const next = prev.filter((item) => item.id !== itemToRemove?.id);
                                if (itemToRemove?.previewUrl) {
                                    revokePendingPreview(itemToRemove.previewUrl);
                                }
                                return next;
                            });
                        }}
                        onRemoveStoredPhoto={removePhoto}
                        disabled={saving || photoActionLoading}
                    />
                </SubSection>

            </Section>

            <Section title="Ekonomika">
                <div className="grid gap-4" style={FORM_GRID_STYLE}>
                    <Forms.Select
                        label="Platba"
                        value={PAYMENT_TYPE_OPTIONS.find(o => o.value === form.economics.paymentType) || null}
                        onChange={option => setF('economics.paymentType', option?.value || 'paid')}
                        options={PAYMENT_TYPE_OPTIONS}
                    />
                </div>
            </Section>

            <Section title="Propojení">
                <div className="grid gap-4" style={FORM_GRID_STYLE}>
                    <Forms.Text label="Repair ID" value={form.links.repairId || ''} onChange={v => setF('links.repairId', v)}/>
                    <Forms.Text label="Inventory item ID" value={form.links.inventoryItemId || ''} onChange={v => setF('links.inventoryItemId', v)}/>
                    <Forms.Text label="Sale order ID" value={form.links.saleOrderId || ''} onChange={v => setF('links.saleOrderId', v)}/>
                </div>
            </Section>

            <div className="pt-2 border-t border-slate-200 flex justify-end">
                {renderActionButtons()}
            </div>
        </div>
    );
};

const Section = ({title, children}) => (
    <div className="p-4 rounded-2xl shadow-sm border bg-white">
        <div className="text-sm font-semibold mb-3">{title}</div>
        {children}
    </div>
);

const SubSection = ({title, children}) => (
    <div className="mt-5">
        <div className="text-xs font-semibold mb-2 opacity-70">{title}</div>
        {children}
    </div>
);

const PhotoManager = ({
    isEdit,
    items,
    pendingFiles,
    onSelectFiles,
    onRemovePendingFile,
    onRemoveStoredPhoto,
    disabled
}) => {
    const pendingGalleryItems = (pendingFiles || []).map((entry, index) => ({
        id: entry.id || `pending_${index}`,
        previewUrl: entry.previewUrl,
        fullUrl: entry.previewUrl,
        width: entry.width,
        height: entry.height,
        name: entry.name,
        meta: [entry.dimensionsLabel, entry.sizeLabel].filter(Boolean).join(' • '),
        alt: `Vybraná fotka ${index + 1}`
    }));

    const uploadedGalleryItems = (items || []).map((item, index) => {
        const previewUrl = item?.variants?.webpThumb?.url
            || item?.variants?.webpFullHd?.url
            || item?.variants?.webpOriginal?.url
            || item?.original?.url
            || null;
        const fullUrl = item?.variants?.webpFullHd?.url
            || item?.variants?.webpOriginal?.url
            || item?.original?.url
            || previewUrl;
        const width = item?.variants?.webpFullHd?.width
            || item?.original?.width
            || item?.variants?.webpThumb?.width
            || null;
        const height = item?.variants?.webpFullHd?.height
            || item?.original?.height
            || item?.variants?.webpThumb?.height
            || null;
        const fileName = item?.original?.path ? item.original.path.split('/').pop() : `Fotka ${index + 1}`;
        const dimensionsLabel = width && height ? `${width}×${height}` : '';
        return {
            id: item?._id || `stored_${index}`,
            previewUrl,
            fullUrl,
            width,
            height,
            name: fileName,
            meta: dimensionsLabel,
            alt: `Nahraná fotka ${index + 1}`
        };
    });

    return (
        <div className="space-y-3">
            <Forms.File
                label="Vyberte fotografie (JPG, PNG, WEBP, HEIC, ...)"
                multiple
                onChange={async (e) => {
                    const fileList = Array.from(e?.target?.files || []);
                    await onSelectFiles(fileList);
                    if (e?.target) {
                        e.target.value = '';
                    }
                }}
                disabled={disabled}
            />

            <PhotoGallery
                title="Vybrané soubory k nahrání při uložení"
                items={pendingGalleryItems}
                emptyMessage="Zatím nebyly vybrány žádné nové fotografie."
                onRemove={(item) => onRemovePendingFile(item)}
                removeLabel="Odebrat"
                disabled={disabled}
            />

            <PhotoGallery
                title="Nahrané fotografie"
                items={uploadedGalleryItems}
                emptyMessage={isEdit ? 'Zatím nejsou nahrané žádné fotografie.' : 'Fotografie bude možné spravovat po prvním uložení výkupu.'}
                onRemove={(item) => onRemoveStoredPhoto(item.id)}
                removeLabel="Smazat"
                disabled={disabled}
            />
        </div>
    );
};

function setDeep(obj, path, value) {
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length - 1; i += 1) {
        const key = parts[i];
        if (typeof current[key] !== 'object' || current[key] === null) {
            current[key] = {};
        }
        current = current[key];
    }
    current[parts[parts.length - 1]] = value;
}

function cloneDeep(value) {
    if (typeof window !== 'undefined' && typeof window.structuredClone === 'function') {
        return window.structuredClone(value);
    }
    if (Array.isArray(value)) {
        return value.map(item => cloneDeep(item));
    }
    if (value instanceof Date) {
        return new Date(value.getTime());
    }
    if (value && typeof value === 'object') {
        const result = {};
        Object.keys(value).forEach((key) => {
            result[key] = cloneDeep(value[key]);
        });
        return result;
    }
    return value;
}

function normalizeIncoming(data) {
    const merged = cloneDeep(DEFAULT_FORM);
    deepMerge(merged, data || {});
    merged.dealAt = normalizeDateValue(merged.dealAt || merged.createdAt || null);
    merged.item.identifiers = normalizeIdentifiers(merged.item?.identifiers);
    merged.economics = {
        purchasePrice: numOrNull(merged.economics?.purchasePrice),
        paymentType: normalizePaymentType(merged.economics?.paymentType)
    };
    merged.item.color = String(merged.item?.color || '').trim();
    merged.item.storage = normalizeStorageValue(
        merged.item?.storage
        || (merged.item?.storageGb ? {storage: merged.item.storageGb, unit: 'GB'} : null)
    );
    merged.device = toId(merged.device);
    merged.source.customerId = toId(merged.source?.customerId);
    merged.acquiredBy = toId(merged.acquiredBy);
    merged.receivedBy = toId(merged.receivedBy || merged.owner || merged.assignedTo);
    delete merged.owner;
    delete merged.assignedTo;
    merged.links.repairId = toId(merged.links?.repairId) || '';
    merged.links.inventoryItemId = toId(merged.links?.inventoryItemId) || '';
    merged.links.saleOrderId = toId(merged.links?.saleOrderId) || '';
    const rawPhotoAssets = Array.isArray(merged.item?.photoIds) ? merged.item.photoIds : [];
    const normalizedPhotoAssets = rawPhotoAssets.filter(item => item && typeof item === 'object');
    const normalizedPhotoIds = rawPhotoAssets.map(toId).filter(Boolean);
    merged.item.photoAssets = normalizedPhotoAssets;
    merged.item.photoIds = normalizedPhotoIds;
    merged.intakePurpose = merged.intakePurpose === 'donor' ? 'donor' : 'repair';
    return merged;
}

function sanitizePayload(form, selectedDevice) {
    const payload = cloneDeep(form);

    payload.device = toId(payload.device);
    payload.source.customerId = payload.source.type === 'customer' ? toId(payload.source.customerId) : null;
    payload.acquiredBy = toId(payload.acquiredBy);
    payload.receivedBy = toId(payload.receivedBy || payload.owner || payload.assignedTo);
    delete payload.owner;
    delete payload.assignedTo;

    if (payload.source.type !== 'external_marketplace') {
        payload.source.marketplace = 'bazos';
        payload.source.sellerName = '';
        payload.source.sellerContact = '';
        payload.source.externalUrl = '';
    }

    payload.links.repairId = payload.links.repairId || null;
    payload.links.inventoryItemId = payload.links.inventoryItemId || null;
    payload.links.saleOrderId = payload.links.saleOrderId || null;
    payload.dealAt = normalizeDateValue(payload.dealAt);

    payload.deviceSnapshot = {
        name: selectedDevice?.name || payload.deviceSnapshot?.name || '',
        code: selectedDevice?.code || payload.deviceSnapshot?.code || ''
    };

    payload.item.identifiers = normalizeIdentifiers(payload.item?.identifiers);
    payload.item.color = String(payload.item?.color || '').trim();
    payload.item.storage = normalizeStorageValue(payload.item?.storage);
    delete payload.item.storageGb;
    payload.item.photoIds = (payload.item?.photoIds || []).map(toId).filter(Boolean);
    delete payload.item.photoAssets;
    payload.economics = {
        purchasePrice: numOrNull(payload.economics?.purchasePrice),
        paymentType: normalizePaymentType(payload.economics?.paymentType)
    };
    delete payload.location;
    delete payload.item.year;

    return payload;
}

function deepMerge(target, source) {
    if (!source || typeof source !== 'object') {
        return target;
    }
    Object.keys(source).forEach((key) => {
        const value = source[key];
        if (Array.isArray(value)) {
            target[key] = value.slice();
            return;
        }
        if (value && typeof value === 'object') {
            target[key] = deepMerge(target[key] || {}, value);
            return;
        }
        target[key] = value;
    });
    return target;
}

function toId(value) {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value._id || value.value || null;
    return null;
}

function numOrNull(v) {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function normalizeIdentifiers(value) {
    if (Array.isArray(value)) {
        const next = {imei: '', serialNumber: ''};
        value.forEach((item) => {
            const type = String(item?.type || '').toLowerCase();
            const identifierValue = String(item?.value || '').trim();
            if (!identifierValue) return;
            if (type === 'imei') next.imei = identifierValue;
            else if (type === 'serial' || type === 'serialnumber' || type === 'sn') next.serialNumber = identifierValue;
        });
        return next;
    }

    const source = value && typeof value === 'object' ? value : {};
    return {
        imei: String(source.imei || '').trim(),
        serialNumber: String(source.serialNumber || source.serial || source.sn || '').trim()
    };
}

function normalizePaymentType(value) {
    const allowed = new Set(['paid', 'cod', 'free']);
    return allowed.has(value) ? value : 'paid';
}

function normalizeDateValue(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function customerLabel(customer) {
    if (!customer) return '';
    if (customer.type === 'company') {
        return customer.company?.name || 'Firma bez názvu';
    }
    const fullName = [customer.person?.name, customer.person?.surname].filter(Boolean).join(' ').trim();
    if (fullName) return fullName;
    return customer.emails?.[0]?.value || customer.phones?.[0]?.value || customer._id;
}

async function buildPendingPhotoEntries(fileList) {
    const files = Array.isArray(fileList) ? fileList.filter(Boolean) : [];
    const entries = await Promise.all(files.map(async (file) => {
        const previewUrl = URL.createObjectURL(file);
        const dimensions = await readImageDimensions(previewUrl);
        return {
            id: `${file.name}_${file.lastModified}_${file.size}_${Math.random().toString(36).slice(2, 8)}`,
            file,
            name: file.name,
            previewUrl,
            width: dimensions?.width || null,
            height: dimensions?.height || null,
            dimensionsLabel: dimensions?.width && dimensions?.height ? `${dimensions.width}×${dimensions.height}` : '',
            sizeLabel: formatFileSize(file.size)
        };
    }));
    return entries;
}

function revokePendingPreview(previewUrl) {
    if (!previewUrl) return;
    URL.revokeObjectURL(previewUrl);
}

function clearPendingPhotos(setter) {
    setter((prev) => {
        prev.forEach((entry) => revokePendingPreview(entry?.previewUrl));
        return [];
    });
}

function formatFileSize(size) {
    const value = Number(size);
    if (!Number.isFinite(value) || value < 0) return '';
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
}

function readImageDimensions(src) {
    return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
            resolve({
                width: image.naturalWidth || null,
                height: image.naturalHeight || null
            });
        };
        image.onerror = () => resolve({width: null, height: null});
        image.src = src;
    });
}

function normalizeDeviceColorOptions(device) {
    const colors = Array.isArray(device?.availableColors) ? device.availableColors : [];
    return colors
        .map((item) => String(item || '').trim())
        .filter(Boolean)
        .map((item) => ({value: item, label: item}));
}

function normalizeDeviceStorageOptions(device) {
    const capacities = Array.isArray(device?.availableStorage) ? device.availableStorage : [];
    const normalized = capacities
        .map((item) => normalizeStorageValue(item))
        .filter(Boolean);

    const unique = new Map();
    normalized.forEach((item) => {
        unique.set(`${item.storage}_${item.unit}`, item);
    });

    return Array.from(unique.values())
        .sort((a, b) => {
            const storageCompare = a.storage - b.storage;
            if (storageCompare !== 0) return storageCompare;
            return a.unit.localeCompare(b.unit);
        })
        .map((item) => ({
            value: `${item.storage}_${item.unit}`,
            label: `${item.storage} ${item.unit}`,
            data: item
        }));
}

function normalizeStorageValue(value) {
    if (!value || typeof value !== 'object') return null;
    const storage = Number(value.storage);
    const unit = String(value.unit || '').toUpperCase();
    if (!Number.isFinite(storage) || storage <= 0) return null;
    if (unit !== 'GB' && unit !== 'TB') return null;
    return {
        storage: Math.round(storage),
        unit
    };
}

const FORM_GRID_STYLE = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))'
};

const COMPACT_FORM_GRID_STYLE = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))'
};

const PrioritySection = ({
    deviceOptions,
    userOptions,
    selectedDeviceOption,
    selectedAcquiredByOption,
    selectedReceivedByOption,
    form,
    setF,
    onDeviceChange
}) => (
    <div className="p-4 rounded-2xl border-2 border-amber-300 bg-amber-50">
        <div className="text-base font-semibold text-amber-900 mb-2">Základní informace</div>
        <div className="mb-4">
            <div className="text-sm font-medium mb-1">Typ výkupu</div>
            <div className="flex flex-col gap-1">
                {INTAKE_PURPOSE_OPTIONS.map((option) => (
                    <label key={option.value} className="inline-flex items-center gap-2 text-sm">
                        <input
                            type="radio"
                            name="intakePurpose"
                            checked={form.intakePurpose === option.value}
                            onChange={() => setF('intakePurpose', option.value)}
                        />
                        <span>{option.label}</span>
                    </label>
                ))}
            </div>
        </div>
        <div className="grid gap-4" style={FORM_GRID_STYLE}>
            <Forms.Select
                label="Zařízení"
                value={selectedDeviceOption}
                onChange={onDeviceChange}
                options={deviceOptions}
            />
            <Forms.Number
                label="Výkupní cena"
                value={form.economics.purchasePrice ?? ''}
                onChange={v => setF('economics.purchasePrice', numOrNull(v))}
            />
            <Forms.Date
                label="Datum výkupu"
                value={form.dealAt || ''}
                onChange={v => setF('dealAt', v || null)}
            />
            <Forms.Select
                label="Vykoupil"
                value={selectedAcquiredByOption}
                onChange={option => setF('acquiredBy', option?.value || null)}
                options={userOptions}
            />
            <Forms.Select
                label="Převzal"
                value={selectedReceivedByOption}
                onChange={option => setF('receivedBy', option?.value || null)}
                options={userOptions}
            />
        </div>
    </div>
);

export default BuybackEditor;
